import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { ContactSummary } from '@/components/console/workspace/ContactSummary';
import { useT } from '@/i18n/labels';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { useCustomerProfile } from '@/hooks/useCustomerProfile';
import { buildHistoryRows } from '@/utils/contactHistory';
import { isAnonymousCustomer } from '@/utils/conversationLabel';
import type {
  CrmInteractionDto,
  InteractionHistoryDto,
  PagedSessionsResponse,
  SessionDto,
} from '@/types';

/** One page of this system's own sessions. 100 keeps the request count low; the cap is 200. */
const PAGE_SIZE = 100;

/**
 * Everything about one contact: who they are, and EVERY conversation they have had.
 *
 * The sidebar's old history list capped at 8 rows and hid the currently-open conversation, both
 * reasonable compromises for a 312px rail and both wrong for a page whose entire purpose is the
 * complete record. So this pages through instead of truncating.
 *
 * <b>Anonymous visitors still get a page.</b> They have no CRM identity, so no lookup happens and no
 * profile is invented — but their sessions are real tenant data and are listed. Refusing the page
 * outright would hide conversations that exist.
 *
 * All three fetches are independent and fail-soft: a CRM outage must not blank the conversation
 * list, and vice versa.
 */
export const ContactPage: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const { customerId = '' } = useParams<{ customerId: string }>();

  const anon = isAnonymousCustomer(customerId);
  const profileState = useCustomerProfile(customerId);

  const [interactions, setInteractions] = useState<CrmInteractionDto[]>([]);
  const [sessions, setSessions] = useState<SessionDto[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // CRM interactions — skipped entirely for an anonymous visitor: there is no identity to resolve,
  // and a stub CRM provider would happily return fabricated rows for one.
  useEffect(() => {
    if (anon || !customerId) {
      setInteractions([]);
      return;
    }
    let cancelled = false;
    setInteractions([]);
    (async () => {
      try {
        const res = await axiosClient.get<InteractionHistoryDto>(
          API.CRM.CUSTOMER_INTERACTIONS(customerId),
        );
        if (!cancelled) setInteractions(res.data?.interactions ?? []);
      } catch {
        /* best-effort — CRM trouble must not blank the conversation list below */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, anon]);

  const loadSessions = useCallback(
    async (targetPage: number, append: boolean) => {
      if (!customerId) return;
      try {
        // No time window and no onlyWithMessages filter — unlike the console's own session load,
        // this is the complete record, including long-closed conversations.
        const res = await axiosClient.get<PagedSessionsResponse>(API.SESSIONS.LIST, {
          params: {
            customerId,
            page: targetPage,
            pageSize: PAGE_SIZE,
            excludeClosed: false,
          },
        });
        const items = res.data?.items ?? [];
        setSessions((prev) => (append ? [...prev, ...items] : items));
        setHasMore(Boolean(res.data?.hasMore));
        setPage(targetPage);
      } catch {
        if (!append) setSessions([]);
        setHasMore(false);
      }
    },
    [customerId],
  );

  useEffect(() => {
    let cancelled = false;
    setLoadingSessions(true);
    setSessions([]);
    setHasMore(false);
    setPage(1);
    loadSessions(1, false).finally(() => {
      if (!cancelled) setLoadingSessions(false);
    });
    return () => {
      cancelled = true;
    };
  }, [loadSessions]);

  const loadMore = async () => {
    setLoadingMore(true);
    await loadSessions(page + 1, true);
    setLoadingMore(false);
  };

  const rows = buildHistoryRows(interactions, sessions, t('ctx_row_conversation_fallback'));

  return (
    <div className="ac-page ac-fade-up ac-contact-page" data-testid="contact-page">
      <button
        type="button"
        className="ac-btn ghost sm ac-contact-back"
        onClick={() => navigate(-1)}
        data-testid="contact-back"
      >
        <Icon name="chevron" size={14} />
        {t('contact_back')}
      </button>

      <div className="ac-card">
        <ContactSummary
          customerId={customerId}
          profile={profileState.profile}
          anonymous={anon}
          loading={profileState.loading}
          notFound={profileState.notFound}
          variant="page"
          avatarSize={72}
        />

        <div className="ac-ctx-sec" style={{ borderBottom: 'none' }}>
          <div className="ac-ctx-sec-title">{t('contact_conversations_title')}</div>

          {loadingSessions ? (
            <div className="ac-ctx-na" data-testid="contact-loading">
              {t('ctx_loading')}
            </div>
          ) : rows.length === 0 ? (
            <div className="ac-ctx-na" data-testid="contact-history-empty">
              {t('ctx_history_empty')}
            </div>
          ) : (
            rows.map((row) => {
              const content = (
                <>
                  <div className="ac-ctx-row-ico">
                    <Icon name={row.icon} size={15} />
                  </div>
                  <div className="ac-ctx-row-main">
                    <div className="ac-ctx-row-title">{row.title}</div>
                    <div className="ac-ctx-row-sub">
                      {`${row.source === 'ticket' ? t('ctx_row_ticket') : t('ctx_row_conversation')} · ${row.sub}`}
                    </div>
                    {/* D7: the links are the SERVER's (previousSessionId), read by buildHistoryRows - never inferred here. */}
                    {row.continuedIn && (
                      <div className="ac-ctx-row-link" data-testid={`ctx-row-continued-in-${row.id}`}>
                        {t('cp_continued_in')}
                      </div>
                    )}
                    {row.continues && (
                      <div className="ac-ctx-row-link" data-testid={`ctx-row-continues-${row.id}`}>
                        {t('cp_continues')}
                      </div>
                    )}
                  </div>
                </>
              );

              // Only conversations are links. A CRM ticket lives in the tenant's own CRM and has no
              // conversation of ours behind it, so it renders as a plain row — a row that looks
              // clickable and does nothing is worse than one that plainly isn't.
              return row.sessionId ? (
                <button
                  key={`${row.source}-${row.id}`}
                  type="button"
                  className="ac-ctx-row link"
                  data-testid={`ctx-row-${row.source}-${row.id}`}
                  aria-label={t('contact_open_conversation')}
                  onClick={() => navigate(`/conversations/${encodeURIComponent(row.sessionId!)}`)}
                >
                  {content}
                </button>
              ) : (
                <div
                  key={`${row.source}-${row.id}`}
                  className="ac-ctx-row"
                  data-testid={`ctx-row-${row.source}-${row.id}`}
                >
                  {content}
                </div>
              );
            })
          )}

          {hasMore && (
            <button
              type="button"
              className="ac-btn ghost sm"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              data-testid="contact-load-more"
              style={{ marginTop: 12 }}
            >
              {t('contact_load_more')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
